import React, { useEffect, useId, useRef, useState } from 'react';
import cx from './cx';

/* ------------------------------------------------------ SegmentedControl */

/** Single-choice toggle group (radiogroup semantics, arrow-key navigation). */
export function SegmentedControl({ options, value, onChange, label, size = 'md', className, fullWidth }) {
  const refs = useRef([]);
  const onKeyDown = (event, index) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    const dir = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
    const next = (index + dir + options.length) % options.length;
    onChange(options[next].value);
    refs.current[next]?.focus();
  };
  return (
    <div role="radiogroup" aria-label={label} className={cx('inline-flex rounded-control border border-line bg-surface-2 p-0.5', fullWidth && 'flex w-full', className)}>
      {options.map((option, index) => {
        const active = option.value === value;
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            ref={(node) => { refs.current[index] = node; }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active || (index === 0 && !options.some((o) => o.value === value)) ? 0 : -1}
            onKeyDown={(event) => onKeyDown(event, index)}
            onClick={() => onChange(option.value)}
            aria-label={option.ariaLabel}
            className={cx(
              'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[8px] font-medium transition-colors',
              size === 'sm' ? 'h-9 px-2.5 text-xs sm:h-8' : 'h-9 px-3 text-sm',
              fullWidth && 'flex-1',
              active ? 'bg-surface text-fg shadow-card' : 'text-fg-muted hover:text-fg'
            )}
          >
            {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ Tabs */

/**
 * WAI-ARIA tabs. `tabs = [{ id, label, icon?, badge? }]`; render panels yourself with
 * `<TabPanel id={id} active={activeId}>` so ids line up.
 */
export function Tabs({ tabs, value, onChange, label, className, idPrefix }) {
  const autoId = useId();
  const prefix = idPrefix || `tabs${autoId}`;
  const refs = useRef([]);
  const onKeyDown = (event, index) => {
    let next = null;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;
    if (next === null) return;
    event.preventDefault();
    onChange(tabs[next].id);
    refs.current[next]?.focus();
  };
  return (
    <div role="tablist" aria-label={label} className={cx('-mb-px flex gap-1 overflow-x-auto border-b border-line', className)}>
      {tabs.map((tab, index) => {
        const active = tab.id === value;
        const Icon = tab.icon;
        return (
          <button
            key={tab.id}
            ref={(node) => { refs.current[index] = node; }}
            id={`${prefix}-tab-${tab.id}`}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={`${prefix}-panel-${tab.id}`}
            tabIndex={active ? 0 : -1}
            onKeyDown={(event) => onKeyDown(event, index)}
            onClick={() => onChange(tab.id)}
            className={cx(
              'inline-flex h-10 shrink-0 items-center gap-2 border-b-2 px-3 text-sm font-medium transition-colors',
              active ? 'border-accent text-fg' : 'border-transparent text-fg-muted hover:text-fg'
            )}
          >
            {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
            {tab.label}
            {tab.badge != null && <span className="rounded-full bg-surface-2 px-1.5 text-2xs text-fg-muted">{tab.badge}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({ id, active, idPrefix, keepMounted = false, className, children }) {
  const isActive = id === active;
  if (!isActive && !keepMounted) return null;
  return (
    <div role="tabpanel" id={`${idPrefix}-panel-${id}`} aria-labelledby={`${idPrefix}-tab-${id}`} tabIndex={0} hidden={!isActive} className={cx('outline-none', className)}>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ Menu */

/**
 * Simple dropdown menu. `trigger` is a render prop receiving button props.
 * `items = [{ label, icon?, onClick, tone?: 'danger', disabled? } | 'divider']`.
 */
export function Menu({ trigger, items, align = 'end', label = 'More actions', className }) {
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const rootRef = useRef(null);
  const itemRefs = useRef([]);
  const menuId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => { if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') { setOpen(false); rootRef.current?.querySelector('[aria-haspopup]')?.focus(); } };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    document.addEventListener('keydown', onKey);
    // Open upwards when there isn't room below (e.g. last row in a bottom sheet).
    const rect = rootRef.current?.getBoundingClientRect();
    if (rect) setDropUp(window.innerHeight - rect.bottom < Math.min(320, items.length * 40 + 16) && rect.top > window.innerHeight - rect.bottom);
    requestAnimationFrame(() => itemRefs.current.find(Boolean)?.focus());
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const actionable = items.filter((item) => item !== 'divider');
  const onItemKey = (event, index) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const dir = event.key === 'ArrowDown' ? 1 : -1;
    const next = (index + dir + actionable.length) % actionable.length;
    itemRefs.current[next]?.focus();
  };

  let actionIndex = -1;
  return (
    <div ref={rootRef} className={cx('relative inline-block', className)}>
      {trigger({
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': open ? menuId : undefined,
        'aria-label': label,
        onClick: () => setOpen((value) => !value),
      })}
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={label}
          className={cx(
            'absolute z-50 min-w-[12rem] overflow-hidden rounded-panel border border-line bg-surface-raised py-1 shadow-pop',
            dropUp ? 'bottom-full mb-1' : 'mt-1',
            align === 'end' ? 'right-0 rtl:left-0 rtl:right-auto' : 'left-0 rtl:right-0 rtl:left-auto'
          )}
        >
          {items.map((item, index) => {
            if (item === 'divider') return <div key={`d${index}`} role="separator" className="my-1 border-t border-line" />;
            actionIndex += 1;
            const myIndex = actionIndex;
            const Icon = item.icon;
            return (
              <button
                key={item.label}
                ref={(node) => { itemRefs.current[myIndex] = node; }}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onKeyDown={(event) => onItemKey(event, myIndex)}
                onClick={() => { setOpen(false); item.onClick?.(); }}
                className={cx(
                  'flex w-full items-center gap-2.5 px-3 py-2 text-start text-sm transition-colors disabled:opacity-50',
                  item.tone === 'danger' ? 'text-loss hover:bg-loss-soft focus:bg-loss-soft' : 'text-fg hover:bg-surface-2 focus:bg-surface-2'
                )}
              >
                {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />}
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
